/**
 * Credit Events Utility
 *
 * Provides a simple event system for broadcasting credit balance updates
 * across the application. This allows components to stay in sync without
 * prop drilling or complex state management.
 */

import { invalidateBillingSummary } from '@/lib/billingSummary';

export const CREDITS_UPDATED_EVENT = 'credits:updated';

// These only announce "I just fetched a fresh balance": nothing changed, so they neither drop the shared summary nor go to other tabs (UAT 1030imp1).
const FETCH_ECHO_SOURCES = new Set(['chat-bar', 'profile-menu']);
const isFetchEcho = (source?: string) => source !== undefined && FETCH_ECHO_SOURCES.has(source);

// Same-origin cross-tab channel — a purchase completed in one tab (e.g. the billing page)
// needs to reach an open project tab that's blocked on "credits exhausted", which a plain
// window CustomEvent can never do since each tab has its own window. BroadcastChannel does
// not deliver back to the sending tab, so same-tab listeners still rely on the CustomEvent.
const CREDITS_CHANNEL_NAME = 'joylo-credits-updated';
let creditsChannel: BroadcastChannel | null = null;

function getCreditsChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === 'undefined') return null;
    if (!creditsChannel) {
        creditsChannel = new BroadcastChannel(CREDITS_CHANNEL_NAME);
        // Registered before any onCreditUpdate listener, so their refetches see the invalidation
        creditsChannel.addEventListener('message', (event: MessageEvent) => {
            if (!isFetchEcho(event.data?.source)) invalidateBillingSummary();
        });
    }
    return creditsChannel;
}

/**
 * Emit a credit update event to notify all listening components — in this tab and,
 * via BroadcastChannel, every other open tab on the same origin — that the credit
 * balance has been refreshed.
 *
 * @param source - Optional identifier of the emitting component so listeners can ignore their own events and avoid refetch loops.
 */
export const emitCreditUpdate = (source?: string) => {
    const echo = isFetchEcho(source);
    if (!echo) invalidateBillingSummary();
    window.dispatchEvent(new CustomEvent(CREDITS_UPDATED_EVENT, { detail: { source } }));
    if (!echo) getCreditsChannel()?.postMessage({ source });
};

/**
 * Listen for credit update events, from this tab and other same-origin tabs.
 *
 * @param callback - Called when credits are updated; receives the emitting source (if any) so callers can skip events they triggered themselves.
 * @returns Cleanup function to remove the event listener
 */
export const onCreditUpdate = (callback: (source?: string) => void) => {
    const handler = (event: Event) => {
        const source = (event as CustomEvent)?.detail?.source;
        callback(source);
    };
    window.addEventListener(CREDITS_UPDATED_EVENT, handler);

    const channel = getCreditsChannel();
    const channelHandler = (event: MessageEvent) => callback(event.data?.source);
    channel?.addEventListener('message', channelHandler);

    // Return cleanup function
    return () => {
        window.removeEventListener(CREDITS_UPDATED_EVENT, handler);
        channel?.removeEventListener('message', channelHandler);
    };
};
